import { Check, Link2, Loader2, X, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { DiscordIcon } from "@/components/icons/BrandIcons";
import { cn } from "@/lib/utils";
import { trpc } from "../../lib/trpc";
import { Badge, Button, Input, Select } from "../ui";
import { ConfirmModal } from "./ConfirmModal";

type ChannelMode = "shared_category" | "per_match_category" | "none";

const channelModeOptions: {
  value: ChannelMode;
  label: string;
  description: string;
}[] = [
  {
    value: "shared_category",
    label: "Shared Category",
    description: "All match channels go under one category",
  },
  {
    value: "per_match_category",
    label: "Per-Match Category",
    description: "Each match gets its own category with channels inside",
  },
  {
    value: "none",
    label: "No Category",
    description: "Channels appear at the top of your server",
  },
];

type Props = {
  clubId: string;
  guildId: string | null;
  categoryId: string | null;
  disputeWebhookChannelId: string | null;
  channelMode: ChannelMode;
  moderatorRoleIds: string[] | null;
  isOwner: boolean;
  slug: string;
};

export function DiscordSection({
  clubId,
  guildId,
  categoryId,
  disputeWebhookChannelId,
  channelMode,
  moderatorRoleIds,
  isOwner,
  slug,
}: Props) {
  const utils = trpc.useUtils();
  const [code, setCode] = useState("");
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  const { data: categories } = trpc.club.getDiscordCategories.useQuery(
    { clubId },
    { enabled: !!guildId },
  );

  const { data: roles } = trpc.club.getDiscordRoles.useQuery({ clubId }, { enabled: !!guildId });
  const { data: textChannels } = trpc.club.getDiscordTextChannels.useQuery(
    { clubId },
    { enabled: !!guildId },
  );

  const linkMutation = trpc.club.linkDiscord.useMutation({
    onSuccess: () => {
      toast.success("Discord server linked!");
      void utils.club.get.invalidate({ slug });
      setCode("");
    },
    onError: (err) => toast.error(err.message || "Failed to link Discord server"),
  });

  const unlinkMutation = trpc.club.unlinkDiscord.useMutation({
    onSuccess: () => {
      toast.success("Discord server unlinked");
      void utils.club.get.invalidate({ slug });
      setShowUnlinkConfirm(false);
    },
    onError: (err) => toast.error(err.message || "Failed to unlink Discord server"),
  });

  const setCategoryMutation = trpc.club.setDiscordCategory.useMutation({
    onSuccess: () => {
      toast.success("Channel category updated");
      void utils.club.get.invalidate({ slug });
    },
    onError: (err) => toast.error(err.message || "Failed to update category"),
  });

  const setChannelModeMutation = trpc.club.setDiscordChannelMode.useMutation({
    onSuccess: () => {
      toast.success("Channel mode updated");
      void utils.club.get.invalidate({ slug });
    },
    onError: (err) => toast.error(err.message || "Failed to update channel mode"),
  });
  const setDisputeWebhookChannelMutation = trpc.club.setDisputeWebhookChannel.useMutation({
    onSuccess: () => {
      toast.success("Dispute alert channel updated");
      void utils.club.get.invalidate({ slug });
    },
    onError: (err) => toast.error(err.message || "Failed to update dispute alert channel"),
  });

  const parsedModeratorRoleIds = useMemo(
    () =>
      Array.isArray(moderatorRoleIds)
        ? moderatorRoleIds
        : typeof moderatorRoleIds === "string"
          ? (JSON.parse(moderatorRoleIds) as string[])
          : [],
    [moderatorRoleIds],
  );

  const [localRoleIds, setLocalRoleIds] = useState<string[]>(() => parsedModeratorRoleIds);

  useEffect(() => {
    setLocalRoleIds(parsedModeratorRoleIds);
  }, [parsedModeratorRoleIds]);

  const setModeratorRolesMutation = trpc.club.setDiscordModeratorRoles.useMutation({
    onSuccess: () => {
      toast.success("Moderator roles updated");
      void utils.club.get.invalidate({ slug });
    },
    onError: (err) => toast.error(err.message || "Failed to update moderator roles"),
  });

  const toggleRole = (roleId: string) => {
    const next = localRoleIds.includes(roleId)
      ? localRoleIds.filter((id) => id !== roleId)
      : [...localRoleIds, roleId];
    setLocalRoleIds(next);
    setModeratorRolesMutation.mutate({ clubId, roleIds: next });
  };

  const testBotMutation = trpc.club.testDiscordBot.useMutation({
    onError: (err) => toast.error(err.message || "Failed to run bot test"),
  });

  const handleLink = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    linkMutation.mutate({ clubId, code: trimmed });
  };

  if (guildId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-discord/15 border border-discord/25">
            <DiscordIcon className="size-5 text-discord" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-white">Discord Server Linked</p>
              <Badge variant="subtle" color="green" size="sm">
                Connected
              </Badge>
            </div>
            <p className="text-xs text-surface-500 mt-0.5 font-mono">{guildId}</p>
          </div>
          {isOwner && (
            <Button
              variant="outline"
              size="sm"
              className="!border-error-500/40 !text-error-400 hover:!bg-error-500/10"
              leftIcon={<X className="size-3.5" />}
              onClick={() => setShowUnlinkConfirm(true)}
            >
              Unlink
            </Button>
          )}
        </div>

        {/* Channel organization mode */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-surface-400">Channel Organization</label>
          <div className="space-y-1.5">
            {channelModeOptions.map((opt) => {
              const isSelected = channelMode === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    if (!isSelected) {
                      setChannelModeMutation.mutate({ clubId, mode: opt.value });
                    }
                  }}
                  disabled={setChannelModeMutation.isPending}
                  className={cn(
                    "flex items-start gap-3 w-full px-3 py-2.5 rounded-lg text-left transition-all",
                    isSelected
                      ? "bg-brand-500/10 border border-brand-500/30"
                      : "border border-surface-700 hover:bg-surface-700/30",
                  )}
                >
                  <div
                    className={cn(
                      "mt-0.5 flex items-center justify-center size-4 rounded-full shrink-0 transition-colors",
                      isSelected
                        ? "bg-brand-500 border-brand-500"
                        : "border-2 border-surface-600 bg-transparent",
                    )}
                  >
                    {isSelected && <div className="size-1.5 rounded-full bg-surface-900" />}
                  </div>
                  <div className="min-w-0">
                    <p
                      className={cn(
                        "text-xs font-semibold",
                        isSelected ? "text-white" : "text-surface-300",
                      )}
                    >
                      {opt.label}
                    </p>
                    <p className="text-xs text-surface-500 mt-0.5">{opt.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Category picker */}
        {channelMode === "shared_category" && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-surface-400">Match Channel Category</label>
            <Select
              value={categoryId ?? ""}
              onChange={(value) => {
                setCategoryMutation.mutate({ clubId, categoryId: value || null });
              }}
              disabled={setCategoryMutation.isPending}
              placeholder="None (server default)"
              options={[
                { value: "", label: "None (server default)" },
                ...(categories ?? []).map((cat) => ({ value: cat.id, label: cat.name })),
              ]}
            />
            <p className="text-xs text-surface-500">
              Match voice and text channels will be created under this category.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-surface-400">Dispute Alert Channel</p>
          <Select
            value={disputeWebhookChannelId ?? ""}
            onChange={(value) => {
              setDisputeWebhookChannelMutation.mutate({
                clubId,
                channelId: value || null,
              });
            }}
            disabled={setDisputeWebhookChannelMutation.isPending}
            placeholder="Disabled"
            options={[
              { value: "", label: "Disabled" },
              ...(textChannels ?? []).map((channel) => ({
                value: channel.id,
                label: `#${channel.name}`,
              })),
            ]}
          />
          <p className="text-xs text-surface-500">
            New disputes and unresolved reminders are posted in this channel.
          </p>
        </div>

        {/* Moderator role picker */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-surface-400">Moderator Roles</label>
          <div className="rounded-lg bg-surface-900/50 border border-surface-700 p-2 space-y-1 max-h-48 overflow-y-auto">
            {roles && roles.length > 0 ? (
              roles.map((role) => {
                const isSelected = localRoleIds.includes(role.id);
                const colorHex = role.color
                  ? `#${role.color.toString(16).padStart(6, "0")}`
                  : undefined;
                return (
                  <button
                    key={role.id}
                    type="button"
                    disabled={setModeratorRolesMutation.isPending}
                    onClick={() => toggleRole(role.id)}
                    className={cn(
                      "flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-md text-xs transition-all text-left",
                      isSelected
                        ? "bg-brand-500/10 border border-brand-500/30"
                        : "border border-transparent hover:bg-surface-700/40",
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-center justify-center size-4 rounded shrink-0 transition-colors",
                        isSelected
                          ? "bg-brand-500 border-brand-500"
                          : "border border-surface-600 bg-surface-800",
                      )}
                    >
                      {isSelected && (
                        <Check className="size-2.5 text-surface-900" strokeWidth={3} />
                      )}
                    </div>
                    <div
                      className="size-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: colorHex ?? "var(--color-discord-muted)" }}
                    />
                    <span
                      className={cn(
                        "flex-1 truncate",
                        isSelected ? "text-white" : "text-surface-300",
                      )}
                    >
                      {role.name}
                    </span>
                  </button>
                );
              })
            ) : (
              <p className="text-xs text-surface-500 px-2 py-1">No roles found</p>
            )}
          </div>
          <p className="text-xs text-surface-500">
            Selected roles can always see match channels (e.g., moderators, admins).
          </p>
        </div>

        {/* Test bot */}
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Zap className="size-3.5" />}
            onClick={() => testBotMutation.mutate({ clubId })}
            disabled={testBotMutation.isPending}
          >
            {testBotMutation.isPending ? "Testing..." : "Test Bot Permissions"}
          </Button>

          {testBotMutation.data && (
            <div className="rounded-lg bg-surface-900/50 border border-surface-700 p-3 space-y-1.5">
              {testBotMutation.data.steps.map((step) => (
                <div key={step.name} className="flex items-center gap-2 text-xs">
                  {step.passed ? (
                    <Check className="size-3.5 text-green-400 shrink-0" />
                  ) : (
                    <X className="size-3.5 text-error-400 shrink-0" />
                  )}
                  <span className={step.passed ? "text-surface-300" : "text-error-400"}>
                    {step.name}
                  </span>
                  {step.error && <span className="text-error-400/70 ml-1">— {step.error}</span>}
                </div>
              ))}
              <p
                className={`text-xs font-medium mt-1 ${testBotMutation.data.passed ? "text-green-400" : "text-error-400"}`}
              >
                {testBotMutation.data.passed
                  ? "All checks passed"
                  : "Some checks failed — verify bot permissions in your server settings"}
              </p>
            </div>
          )}
        </div>

        {showUnlinkConfirm && (
          <ConfirmModal
            open={true}
            onClose={() => setShowUnlinkConfirm(false)}
            title="Unlink Discord"
            description="Are you sure? The bot will no longer create match channels or move players in this Discord server."
            confirmLabel="Unlink"
            isPending={unlinkMutation.isPending}
            variant="danger"
            icon={<X className="size-5 text-error-400" />}
            onConfirm={() => unlinkMutation.mutate({ clubId })}
          />
        )}
      </div>
    );
  }

  // Not linked state
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-surface-700/50 border border-surface-600/40">
          <DiscordIcon className="size-5 text-surface-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-white">Link Discord Server</p>
          <p className="text-xs text-surface-500">
            Connect your Discord for match voice channels and notifications
          </p>
        </div>
      </div>

      <ol className="space-y-3 mt-1">
        <li className="flex gap-3">
          <span className="flex items-center justify-center size-6 rounded-full bg-surface-700/60 text-xs font-bold text-surface-400 shrink-0 mt-0.5">
            1
          </span>
          <div className="min-w-0">
            <p className="text-sm text-surface-300">
              <a
                href={
                  import.meta.env.VITE_DISCORD_CLIENT_ID
                    ? `https://discord.com/oauth2/authorize?client_id=${import.meta.env.VITE_DISCORD_CLIENT_ID}&permissions=17845264&scope=bot%20applications.commands`
                    : "#"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-brand-400 hover:text-brand-300 transition-colors"
              >
                Invite the Rivaled bot
              </a>{" "}
              to your Discord server
            </p>
            <p className="text-xs text-surface-500 mt-0.5">Requires Manage Server permission</p>
          </div>
        </li>

        <li className="flex gap-3">
          <span className="flex items-center justify-center size-6 rounded-full bg-surface-700/60 text-xs font-bold text-surface-400 shrink-0 mt-0.5">
            2
          </span>
          <div className="min-w-0">
            <p className="text-sm text-surface-300">
              Run{" "}
              <code className="px-1.5 py-0.5 rounded bg-surface-700 text-brand-400 font-mono text-xs">
                /rivaled-setup
              </code>{" "}
              in any channel
            </p>
            <p className="text-xs text-surface-500 mt-0.5">
              You'll get a 6-character code (expires in 15 min)
            </p>
          </div>
        </li>

        <li className="flex gap-3">
          <span className="flex items-center justify-center size-6 rounded-full bg-surface-700/60 text-xs font-bold text-surface-400 shrink-0 mt-0.5">
            3
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-surface-300 mb-2">Enter the code below</p>
            <form onSubmit={handleLink} className="flex items-center gap-2">
              <Input
                placeholder="ABC123"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={6}
                className="!w-36 font-mono tracking-[0.25em] text-center !uppercase"
              />
              <Button
                type="submit"
                variant="primary"
                disabled={code.trim().length < 6 || linkMutation.isPending}
                leftIcon={
                  linkMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Link2 className="size-4" />
                  )
                }
              >
                {linkMutation.isPending ? "Linking..." : "Link Server"}
              </Button>
            </form>
          </div>
        </li>
      </ol>
    </div>
  );
}
